import React from 'react';
import styled from 'styled-components';
import { THEME_COLORS } from '../common/colors';

const Title = styled.h1`
  margin-bottom: 8px;
  text-align: center;
`;

const Subtitle = styled.h3`
  margin: 8px;
`;

const Summary = styled.p`
  margin-bottom: 8px;
  text-align: left;
  line-height: 24px;
`;

const SubSummary = styled.p`
  margin-bottom: 20px;
  padding-left: 8px;
  line-height: 24px;
`;

const PageContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 36px;
  background-color: #fff;
`;

const AgreementList = styled.ol`
  margin: 12px;
  margin-bottom: 20px;
  padding-left: 16px;
  li {
    margin-bottom: 8px;
    line-height: 24px;
  }
`;

export function PrivacyPolicy() {
  return (
    <div style={{ backgroundColor: THEME_COLORS.backgroundDecorator }}>
      <PageContainer>
        <Title>WeSuap's Privacy Policy</Title>
        <Summary>
          <b>Effective Date: 01.01.2025</b> <br />
          At WeSuap, we believe privacy is a right — not a privilege. We're
          committed to treating your data with care, transparency, and respect.
          This Privacy Policy explains how we collect, use, and protect your
          information. By using our services, you agree to this policy. <br />
          <b>
            We don't sell your data. We don't exploit your trust. We're here to
            help you grow — not track you.
          </b>{' '}
          <br />
        </Summary>
        <Subtitle>1. Information We Collect</Subtitle>
        <SubSummary>
          We only collect what’s needed to support your experience and help you
          get the most from WeSuap.
          <br />
          <b>Information You Provide:</b> <br />
          <ul>
            <li>
              Name, email address, phone number, age (for eligibility), pronouns
            </li>
            <li>Skills, goals, interests, and what you offer or need</li>
            <li>
              Communications you send us or share on the Platform, including
              messages, offers, and support requests
            </li>
          </ul>
          <b>Information Collected Automatically:</b> <br />
          <ul>
            <li>Device and browser information, IP address</li>
            <li>Pages visited, interactions, timestamps</li>
            <li>
              Cookie and similar technologies to improve performance and
              personalize your experience
            </li>
          </ul>
        </SubSummary>
        <Subtitle>2. Why We Collect It</Subtitle>
        <SubSummary>
          We use your data to: <br />
          <ul>
            <li>Help you connect with the right people and opportunities</li>
            <li>Personalize your experience based on your goals</li>
            <li>Improve the platform and understand what's working</li>
            <li>Communicate essential updates or match notifications</li>
            <li>Keep our community safe and prevent fraud or abuse</li>
          </ul>
          <b>
            We never use your personal data for third-party ads or surveillance.
          </b>
        </SubSummary>
        <Subtitle>3. Communications & Messaging Data</Subtitle>
        <SubSummary>
          <b>
            If you provide a phone number, WeSuap may use it to send
            transactional messages, such as:
          </b>{' '}
          <br />
          <ul>
            <li>One-time passcodes (OTP) for authentication</li>
            <li>
              Notifications about offers, matches, exchanges, or account
              activity
            </li>
            <li>Security alerts or important service notices</li>
          </ul>
          If you explicitly opt in, we may also send marketing or discovery
          messages, such as updates about new features or offers you may be
          interested in.
          <br />
          <b>
            Message frequency varies. Message and data rates may apply.
          </b>{' '}
          <br />
          You may opt out of marketing messages at any time by replying STOP to
          any marketing SMS. You may reply HELP for assistance. Opting out of
          marketing messages does not affect transactional or security messages.
        </SubSummary>
        <Subtitle>4. When (and Why) We Share</Subtitle>
        <SubSummary>
          We keep your information private, except in the following limited
          cases: <br />
          <ul>
            <li>With your consent, to complete a connection or exchange</li>
            <li>
              With trusted service providers (such as hosting, analytics, and
              communications providers like Twilio) who process data on our
              behalf and only as needed to perform their services
            </li>
            <li>
              When required by law, subpoena, or to protect rights, safety, or
              security
            </li>
            <li>
              As anonymized or aggregated data for ethical research or reporting
              — never tied back to you
            </li>
          </ul>
        </SubSummary>
        <Subtitle>5. Your Rights</Subtitle>
        <SubSummary>
          You control your data. You can:
          <ul>
            <li>Access, correct, or delete your info at any time</li>
            <li>Request a copy of your data</li>
            <li>
              Opt out of marketing or non-essential communications, including
              SMS marketing messages
            </li>
            <li>Deactivate or delete your account</li>
          </ul>
          Contact us at hello@wesuap.com and we'll respond within 30 days.
        </SubSummary>
        <Subtitle>6. How We Protect Your Data</Subtitle>
        <SubSummary>
          We use industry-standard security practices, including:
          <ul>
            <li>Data encryption</li>
            <li>Limited internal access</li>
            <li>Secure servers and backups</li>
            <li>Routine security monitoring</li>
          </ul>
          <b>No system is 100% invulnerable. </b>Please use a strong password
          and be mindful of the information you share.
        </SubSummary>
        <Subtitle>7. Data Retention</Subtitle>
        <SubSummary>
          We keep your data only as long as needed for you to use WeSuap or as
          required by law. When you delete your account, your personal data is
          deleted or anonymized within 30 days.
        </SubSummary>
        <Subtitle>8. No Guarantee or Endorsements</Subtitle>
        <SubSummary>
          WeSuap is a platform for connection and skill exchange. We are{' '}
          <b>not responsible for the actions, quality, or conduct of users</b>{' '}
          on or off the platform. You use WeSuap at your own discretion and
          risk. <br />
          We <b>do not guarantee</b> any outcomes or results. We encourage
          mutual respect but can’t control individual behavior.
        </SubSummary>
        <SubSummary>
          Communications data (including SMS metadata such as timestamps and
          delivery status) is retained only as long as necessary to provide
          messaging services, meet legal obligations, resolve disputes, or
          enforce our agreements.
        </SubSummary>
        <Subtitle>9. Legal Protections & Limits</Subtitle>
        <SubSummary>
          To minimize legal risk and ensure fairness:
          <ul>
            <li>WeSuap is provided "as is" and "as available"</li>
            <li>We make no warranties about performance or results</li>
            <li>WeSuap is not liable for indirect or consequential damages</li>
            <li>
              Any liability is limited to $100 or the amount you've paid
              (whichever is less)
            </li>
            <li>
              You agree to resolve disputes informally and indemnify us for
              misuse of the platform
            </li>
          </ul>
        </SubSummary>
        <Subtitle>10. Who Can Use WeSuap</Subtitle>
        <SubSummary>
          WeSuap is intended for adults (18+). We do not knowingly collect data
          from children under 18. If you are a parent and believe your child
          used WeSuap, please contact us so we can remove the account.
        </SubSummary>
        <Subtitle>10. International Users</Subtitle>
        <SubSummary>
          We are based in the U.S. If you access WeSuap from outside the U.S.,
          you consent to your data being transferred and processed here, where
          privacy laws may differ from your country.
        </SubSummary>
        <Subtitle>12. Changes to This Policy</Subtitle>
        <SubSummary>
          We may update this Privacy Policy over time. We'll revise the
          “Effective Date” above and notify you of significant changes.
          Continued use of WeSuap means you accept the updated policy.
        </SubSummary>
        <Subtitle>13. Contact Us</Subtitle>
        <SubSummary>
          We're here to help. <br />
          Email: hello@WeSuap.com
        </SubSummary>
        <SubSummary>
          <b>
            This Privacy Policy is provided for transparency and clarity. It
            does not constitute legal advice. We recommend consulting legal
            counsel for compliance in your specific jurisdiction (e.g. GDPR,
            CCPA).
          </b>
        </SubSummary>
      </PageContainer>
    </div>
  );
}
